import { useEffect, useState } from 'react';
import { login } from 'wasp/client/auth';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '../../v32/components/ui/card';
import { Input } from '../../v32/components/ui/input';
import { Label } from '../../v32/components/ui/label';
import { Button } from '../../v32/components/ui/button';
import { track as trackAnalytics } from '../../analytics';
import { GoogleSignInButton } from 'wasp/client/auth'
import { getEmailByUsername } from 'wasp/client/operations';

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try { trackAnalytics('auth_view', { page: 'login' }) } catch {}
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const identifier = usernameOrEmail.trim();
      const isEmail = identifier.includes('@');
      let emailToUse = identifier;
      
      if (!isEmail) {
        // Username was entered, look up the email
        try {
          const result = await getEmailByUsername({ username: identifier });
          if (!result.email) {
            setError('Invalid username or password');
            return;
          }
          emailToUse = result.email;
        } catch (lookupError) {
          // Don't reveal whether username exists - use generic error
          setError('Invalid username or password');
          return;
        }
      }
      
      // Now login with the email (either directly provided or looked up)
      await login({ email: emailToUse, password });
      try { trackAnalytics('auth_submit', { page: 'login', success: true }) } catch {}
      
      // Redirect to dashboard - let Wasp handle auth redirects
      navigate('/');
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 401 || status === 422) {
        setError('Invalid email or password');
      } else {
        setError(err?.message || 'Unable to sign in');
      }
      try { trackAnalytics('auth_submit', { page: 'login', success: false }) } catch {}
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-sand flex items-center justify-center px-4 py-6">
      <Card className="w-full max-w-md border-0 shadow-lg rounded-2xl">
        <CardHeader className="text-center space-y-2">
          <div className="text-3xl font-bold text-dusk">nura</div>
          <h1 className="text-2xl font-semibold tracking-tight text-dusk">Welcome back</h1>
          <p className="text-muted-foreground text-sm">Ready to plan your next delicious week?</p>
        </CardHeader>
        <CardContent className="space-y-6">
          <form onSubmit={onSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="usernameOrEmail" className="text-[14px]">Username or Email</Label>
                  <Input 
                    id="usernameOrEmail" 
                    type="text" 
                    value={usernameOrEmail} 
                    onChange={e => setUsernameOrEmail(e.target.value)} 
                    required 
                    placeholder="Username or Email"
                    className="h-[56px] mt-[8px] text-[16px] placeholder:text-[16px]"
                  />
                </div>
                <div>
                  <Label htmlFor="password" className="text-[14px]">Password</Label>
                  <Input 
                    id="password" 
                    type="password" 
                    value={password} 
                    onChange={e => setPassword(e.target.value)} 
                    required 
                    className="h-[56px] mt-[8px] text-[16px] placeholder:text-[16px]"
                  />
                </div>
                {error && <p className="text-[10px] text-red-600 mt-[8px]">{error}</p>}
                <Button type="submit" className="w-full h-11 text-base font-medium" disabled={submitting}>
                  {submitting ? 'Signing in…' : 'Sign in'}
                </Button>
              </form>

              {/* OAuth divider */}
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <div className="h-px bg-stone flex-1" />
                <span className="text-xs text-muted-foreground">or</span>
                <div className="h-px bg-stone flex-1" />
              </div>

              {/* Google OAuth (Wasp-managed) */}
              <GoogleSignInButton />

              <div className="text-center text-sm space-y-1">
                <span className="text-muted-foreground">Don't have an account? </span>
                <Link to="/signup" className="text-spice hover:text-spice/80 font-medium text-sm tracking-wide">Create account</Link>
                <div>
                  <Link to="/request-password-reset" className="text-spice hover:text-spice/80 text-xs">Forgot your password?</Link>
                </div>
              </div>
              <p className="text-center text-[10px] text-muted-foreground leading-relaxed">
                By signing in, you agree to our{' '}
                <a href="/legal/terms" className="text-spice hover:underline">Terms of Use</a>{' '}
                and{' '}
                <a href="/legal/privacy" className="text-spice hover:underline">Privacy Policy</a>.
              </p>
        </CardContent>
      </Card>
    </div>
  );
}
